import { Colors, ConfirmDialog, Pagination, Spinner, Text } from '@amelio/ui-library';

import { NoteCard } from './NoteCard';
import { NoteComposer } from './NoteComposer';
import { useEmployeeNoteList } from './useEmployeeNoteList';

import type { EmployeeNoteWriteErrors } from './useEmployeeNoteList';
import type {
    EmployeeNoteCategory,
    EmployeeNoteDraft,
    EmployeeNoteOriginFilter,
} from '@/types/employee-note';

import { useAmelioTranslations } from '@/hooks';

/** The texts a list speaks in, owned by the screen that shows it. */
export interface EmployeeNoteListLabels extends EmployeeNoteWriteErrors {
    empty: string;
    placeholder: string;
    deleteTitle: string;
    deleteMessage: string;
    loadError: string;
    forbidden: string;
    pageSummary: (range: { start: number; end: number; total: number }) => string;
}

interface EmployeeNoteListProps {
    employeeId: string;
    /** One folder, or `null` for every folder. */
    category: EmployeeNoteCategory | null;
    /** Narrows the list to the notes written in one meeting or assessment; new notes carry it too. */
    origin?: EmployeeNoteOriginFilter;
    /** The folder a note added from this list is filed under. */
    newNoteCategory: EmployeeNoteCategory;
    pageSize: number;
    /** Shows the notes without the means to add, edit or delete one. */
    readOnly?: boolean;
    labels: EmployeeNoteListLabels;
    /** Receives the means to save the note being written, or `null` while none is. */
    onUnsavedNoteChange?: (saveNote: (() => Promise<boolean>) | null) => void;
    dataTestId: string;
}

/**
 * A paged list of an employee's notes with the means to add, edit and delete them, as the server
 * allows each one. It fetches its own notes and their authors, so a screen only has to say which
 * notes it shows and in which words.
 *
 * The editor is open from the start, under the notes already written: a section opened to write in
 * asks for nothing before it can be written in.
 */
export const EmployeeNoteList = ({
    employeeId,
    category,
    origin = {},
    newNoteCategory,
    pageSize,
    readOnly = false,
    labels,
    onUnsavedNoteChange,
    dataTestId,
}: EmployeeNoteListProps) => {
    const { t } = useAmelioTranslations();
    const {
        notes,
        authorsById,
        isLoading,
        isError,
        isForbidden,
        page,
        setPage,
        totalCount,
        totalPages,
        pageRange,
        draftKey,
        startNewDraft,
        createNote,
        isCreating,
        updateNote,
        isSavingNote,
        noteIdToDelete,
        setNoteIdToDelete,
        confirmDelete,
        isDeleting,
    } = useEmployeeNoteList({
        employeeId,
        category,
        pageSize,
        origin,
        writeErrors: labels,
    });

    const saveNewNote = async (draft: EmployeeNoteDraft) => {
        const isSaved = await createNote(draft);
        if (isSaved) {
            onUnsavedNoteChange?.(null);
        }
        return isSaved;
    };

    const reportDraft = (draft: EmployeeNoteDraft | null) =>
        onUnsavedNoteChange?.(draft && (() => saveNewNote(draft)));

    if (isLoading) {
        return (
            <div
                className={'flex justify-center py-6'}
                data-testid={`${dataTestId}-loading`}
            >
                <Spinner
                    size={'md'}
                    color={Colors.PRIMARY}
                />
            </div>
        );
    }

    // An unread list is not an empty one: say why nothing is shown, and offer no way to write.
    if (isError) {
        return (
            <Text
                size={'small'}
                color={Colors.BASE_500}
            >
                {isForbidden ? labels.forbidden : labels.loadError}
            </Text>
        );
    }

    // Every note of a list narrowed to one origin comes from it: a link back would lead nowhere new.
    const isNarrowedToOrigin = !!origin.thematicMeetingId || !!origin.cycleInstanceId;

    return (
        <div
            className={'flex flex-col gap-6'}
            data-testid={dataTestId}
        >
            {notes.length === 0 ? (
                <Text
                    size={'small'}
                    color={Colors.BASE_500}
                >
                    {labels.empty}
                </Text>
            ) : (
                <ul className={'flex flex-col gap-4'}>
                    {notes.map((note) => (
                        <li key={note.id}>
                            <NoteCard
                                note={note}
                                author={note.authorId ? authorsById.get(note.authorId) : undefined}
                                header={'identity'}
                                readOnly={readOnly}
                                showOrigin={!isNarrowedToOrigin}
                                editorPlaceholder={labels.placeholder}
                                onSave={(draft) => updateNote(note, draft)}
                                onRequestDelete={() => setNoteIdToDelete(note.id)}
                                isSaving={isSavingNote(note.id)}
                                dataTestId={`${dataTestId}-note-${note.id}`}
                            />
                        </li>
                    ))}
                </ul>
            )}

            {totalCount > pageSize && (
                <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    pageSize={pageSize}
                    onPageChange={setPage}
                    resultsSummaryText={labels.pageSummary(pageRange)}
                    dataTestId={`${dataTestId}-pagination`}
                />
            )}

            {!readOnly && (
                <NoteComposer
                    key={draftKey}
                    category={newNoteCategory}
                    placeholder={labels.placeholder}
                    onSave={(draft) => void saveNewNote(draft)}
                    onCancel={() => {
                        startNewDraft();
                        reportDraft(null);
                    }}
                    onDraftChange={reportDraft}
                    isSaving={isCreating}
                    dataTestId={`${dataTestId}-composer`}
                />
            )}

            <ConfirmDialog
                isOpen={!!noteIdToDelete}
                title={labels.deleteTitle}
                message={labels.deleteMessage}
                confirmLabel={t('performanceProfile.employeeNotes.delete')}
                cancelLabel={t('common.cancel')}
                onConfirm={confirmDelete}
                onCancel={() => setNoteIdToDelete(null)}
                isLoading={isDeleting}
                isDestructive
                dataTestId={`${dataTestId}-delete-dialog`}
            />
        </div>
    );
};
